import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import AppPageTitle from "../AppPageTitle";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import { resolveMediaUrl } from "../../helpers/resolveMediaUrl";
import BulkMessagingCampaignStep from "./BulkMessagingCampaignStep";
import BulkMessagingContactsStep from "./BulkMessagingContactsStep";
import BulkMessagingReviewStep from "./BulkMessagingReviewStep";
import {
	bulkContactKey,
	isBulkContactsImportFile,
	isBulkContactsSpreadsheetFile,
	isValidBulkManualPhone,
	isWhatsappBulkContact,
	normalizeBulkPhoneDigits,
	parseCsvContacts,
	parseSpreadsheetContacts,
} from "./bulkMessagingUi";
import { VariablesModal } from "./VariablesModal";

const defaultVariables = ["${contactName}"];

const defaultBulkMessage = {
	bulkMessagingData: {
		name: "",
		trigger: new Date().toISOString().split(".")[0].substring(0, 16),
		status: "",
		message: "",
		mediaPath: null,
		messageInterval: 5,
		clusterSize: 5,
		clusterInterval: 5,
		connectionId: null,
		scheduled: false,
		tagIds: [],
	},
	contacts: [],
};

function getSteps() {
	return [
		i18n.t("bulkMessaging.modal.steps.campaign"),
		i18n.t("bulkMessaging.modal.steps.contacts"),
		i18n.t("bulkMessaging.modal.steps.review"),
	];
}

export default function BulkMessagingForm({ bulkMessageId, onCancel, onSaved }) {
	const [activeStep, setActiveStep] = useState(0);
	const [completed, setCompleted] = useState({});
	const [bulkMessage, setBulkMessage] = useState(defaultBulkMessage);
	const [newUser, setNewUser] = useState({
		name: "",
		number: "",
	});
	const [addContactsType, setAddContactsType] = useState("leads");
	const [triggerType, setTriggerType] = useState("immediate");
	const [csvName, setCsvName] = useState("");
	const [lastCsvImportCount, setLastCsvImportCount] = useState(0);
	const [csvImportRejected, setCsvImportRejected] = useState([]);
	const includedPanelRef = useRef(null);
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [contacts, setContacts] = useState([]);
	const [toTransferContacts, setToTransferContacts] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [searchParam, setSearchParam] = useState("");
	const searchParamRef = useRef(searchParam);
	const selectedTagsRef = useRef(selectedTags);
	searchParamRef.current = searchParam;
	selectedTagsRef.current = selectedTags;
	const [contactsPage, setContactsPage] = useState(1);
	const [hasMoreContacts, setHasMoreContacts] = useState(false);
	const [loadingContacts, setLoadingContacts] = useState(false);
	const [variablesModalOpen, setVariablesModalOpen] = useState(false);
	const [tags, setTags] = useState([]);
	const [showEmoji, setShowEmoji] = useState(false);
	const [channel, setChannel] = useState();
	const { whatsApps } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const [templateParamsLength, setTemplateParamsLength] = useState(0);
	const [templateParams, setTemplateParams] = useState([]);
	const steps = getSteps();

	const totalSteps = () => steps.length;

	const completedSteps = () => Object.keys(completed).length;

	const isLastStep = () => activeStep === totalSteps() - 1;

	const allStepsCompleted = () => completedSteps() === totalSteps();

	const getDateFormat = (oldDate) => {
		const date = new Date(oldDate);
		const year = date.getFullYear();
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		const day = date.getDate().toString().padStart(2, "0");
		const hour = date.getHours().toString().padStart(2, "0");
		const minute = date.getMinutes().toString().padStart(2, "0");
		const newDate = `${year}-${month}-${day}T${hour}:${minute}`;

		return newDate;
	};

	const resetForm = () => {
		setBulkMessage(defaultBulkMessage);
		setCompleted({});
		setChannel(null);
		setActiveStep(0);
		setTriggerType("immediate");
		setAddContactsType("manual");
		setNewUser({
			name: "",
			number: "",
		});
	};

	const handleCancel = () => {
		resetForm();
		if (onCancel) onCancel();
	};

	const countTemplateParams = (text) => {
		var regex = /\{{\d+}}/g;
		var matches = text?.match(regex);

		if (matches) {
			return matches.length;
		} else {
			return 0;
		}
	};

	const handleNext = () => {
		const newActiveStep =
			isLastStep() && !allStepsCompleted()
				? // It's the last step, but not all steps have been completed,
				// find the first step that has been completed
				steps.findIndex((step, i) => !(i in completed))
				: activeStep + 1;
		setActiveStep(newActiveStep);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleStep = (step) => () => {
		setActiveStep(step);
	};

	const handleComplete = () => {
		const newCompleted = completed;
		newCompleted[activeStep] = true;
		setCompleted(newCompleted);
		handleNext();
	};

	const handleReset = () => {
		setActiveStep(0);
		setCompleted({});
		setBulkMessage(defaultBulkMessage);
		setNewUser({
			name: "",
			number: "",
		});
	};

	const handleFinish = async () => {
		const bulkMessagingData = {
			...bulkMessage.bulkMessagingData,
			templateParams:
				Array.isArray(templateParams) && templateParams.length > 0
					? templateParams
					: bulkMessage.bulkMessagingData.templateParams,
		};

		if (
			bulkMessagingData.templateParams === "null" ||
			bulkMessagingData.templateParams == null
		) {
			delete bulkMessagingData.templateParams;
		}

		const payloadBase = {
			...bulkMessage,
			bulkMessagingData,
		};

		const companyParams =
			user?.companyId != null
				? { params: { companyId: user.companyId } }
				: {};

		if (bulkMessageId) {
			try {
				const payload = {
					...payloadBase,
					contacts: (bulkMessage.contacts || []).map(({ name, number }) => ({
						name,
						number,
					})),
					bulkMessagingData: {
						...bulkMessagingData,
						id: Number(bulkMessageId),
					},
				};
				await apiV2.put(
					`/bulk-messaging/${bulkMessageId}`,
					payload,
					companyParams
				);
				toast.success(i18n.t("bulkMessaging.toasts.updated"));
			} catch (error) {
				toastError(error);
				return;
			}
		}

		if (!bulkMessageId) {
			try {
				const { data } = await apiV2.post("/bulk-messaging", payloadBase, companyParams);
				const { invalidContacts } = data;

				if (invalidContacts?.length) {
					const invalids = invalidContacts.map((contact) => contact.name);
					toast.error(
						`Os seguintes contatos são inválidos: ${invalids.join(', ')}`,
						{ autoClose: false }
					)
				}
			} catch (error) {
				toastError(error);
				return;
			}
		}

		resetForm();
		if (onSaved) onSaved();
	};

	const handleUpdateMedia = async (e) => {
		if (!e.target.files) {
			return;
		}

		const img = e.target.files[0];
		e.preventDefault();
		const formData = new FormData();
		formData.append("file", img);
		const response = await api.post("/bulk-messaging/upload-image", formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

		const url = resolveMediaUrl(img.name);

		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				mediaPath: url,
			},
		});
	};

	const handleCreateContact = () => {
		const normalizedNumber = normalizeBulkPhoneDigits(newUser.number);
		if (!isValidBulkManualPhone(normalizedNumber)) return;

		const contact = {
			name: newUser.name?.trim() || normalizedNumber,
			number: normalizedNumber,
		};
		const key = bulkContactKey(contact);
		if (!key) return;

		const alreadyInCampaign = (bulkMessage?.contacts || []).some(
			(c) => bulkContactKey(c) === key
		);
		if (alreadyInCampaign) return;

		setBulkMessage({
			...bulkMessage,
			contacts: [...(bulkMessage?.contacts || []), contact],
		});
		setNewUser({ name: "", number: "" });
	};

	const applyParsedImport = (parsed) => {
		if (parsed.error === "empty") {
			toast.error(i18n.t("bulkMessaging.wizard.contactsStep.importEmpty"));
			return;
		}

		if (parsed.error === "invalidColumns") {
			toast.error(i18n.t("bulkMessaging.wizard.contactsStep.importInvalidColumns"));
			return;
		}

		if (parsed.error === "noRows") {
			toast.error(i18n.t("bulkMessaging.wizard.contactsStep.importNoRows"));
			return;
		}

		const rejectedRows = [...(parsed.rejected || [])];
		const existing = Array.isArray(bulkMessage?.contacts) ? bulkMessage.contacts : [];
		const existingNumbers = new Set(
			existing.map((contact) => bulkContactKey(contact)).filter(Boolean)
		);
		const importedContacts = [];

		(parsed.contacts || []).forEach((contact) => {
			const key = bulkContactKey(contact);
			if (!key) return;

			if (existingNumbers.has(key)) {
				rejectedRows.push({
					line: null,
					name: contact.name,
					rawNumber: contact.number,
					reason: "alreadyInCampaign",
				});
				return;
			}

			existingNumbers.add(key);
			importedContacts.push(contact);
		});

		if (importedContacts.length > 0) {
			setBulkMessage((prev) => {
				const prevContacts = Array.isArray(prev?.contacts) ? prev.contacts : [];
				const prevKeys = new Set(
					prevContacts.map((contact) => bulkContactKey(contact)).filter(Boolean)
				);
				const toAdd = importedContacts.filter((contact) => {
					const key = bulkContactKey(contact);
					return key && !prevKeys.has(key);
				});
				if (!toAdd.length) return prev;
				return {
					...prev,
					contacts: [...prevContacts, ...toAdd],
				};
			});
		}

		setCsvImportRejected(rejectedRows);
		setLastCsvImportCount(importedContacts.length);

		if (importedContacts.length > 0) {
			if (rejectedRows.length > 0) {
				toast.warn(
					i18n.t("bulkMessaging.wizard.contactsStep.importPartialSuccess", {
						valid: importedContacts.length,
						invalid: rejectedRows.length,
					})
				);
			} else {
				toast.success(
					i18n.t("bulkMessaging.wizard.contactsStep.importSuccess", {
						count: importedContacts.length,
					})
				);
			}
			includedPanelRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		} else {
			toast.error(i18n.t("bulkMessaging.wizard.contactsStep.importNoRows"));
		}
	};

	const setContactsByCSV = (e) => {
		const file = e?.target?.files?.[0];
		if (!file) return;

		if (!isBulkContactsImportFile(file.name)) {
			toast.error(i18n.t("bulkMessaging.wizard.contactsStep.importUnsupportedType"));
			if (e?.target) e.target.value = "";
			return;
		}

		setLoadingContacts(true);
		setCsvName(file.name);
		setCsvImportRejected([]);
		setLastCsvImportCount(0);

		const finish = () => {
			setLoadingContacts(false);
			if (e?.target) e.target.value = "";
		};

		const reader = new FileReader();

		reader.onerror = () => {
			finish();
			toast.error(i18n.t("bulkMessaging.wizard.contactsStep.importError"));
		};

		if (isBulkContactsSpreadsheetFile(file.name)) {
			reader.onload = async (event) => {
				try {
					const buffer = event.target?.result;
					const parsed = await parseSpreadsheetContacts(buffer);
					applyParsedImport(parsed);
				} catch (error) {
					toastError(error);
				} finally {
					finish();
				}
			};
			reader.readAsArrayBuffer(file);
			return;
		}

		reader.onload = (event) => {
			try {
				const parsed = parseCsvContacts(event.target?.result);
				applyParsedImport(parsed);
			} catch (error) {
				toastError(error);
			} finally {
				finish();
			}
		};

		reader.readAsText(file, "UTF-8");
	};

	const formatContactsPage = (apiContacts, effectiveChannel) => {
		console.log("effectiveChannel",effectiveChannel)
		if (effectiveChannel === "whatsapp" || effectiveChannel === "whatsappcloud" || effectiveChannel === "whatsapp_business_account") {
				
			return (apiContacts || [])
				.filter(isWhatsappBulkContact)
				.map((contact) => ({
					id: contact.id,
					name: contact.name,
					number: contact.number,
				}));
		}
		if (effectiveChannel === "telegram") {
			return (apiContacts || [])
				.filter((contact) => contact.channel === "telegram")
				.map((contact) => ({
					name: contact.name,
					number: "",
				}));
		}
		if (effectiveChannel === "email") {
			return (apiContacts || [])
				.filter((contact) => Boolean(contact.email))
				.map((contact) => ({
					name: contact.name,
					number: contact.email,
				}));
		}
		return [];
	};

	const getContacts = useCallback(async () => {
		const effectiveChannel =
			channel || bulkMessage?.bulkMessagingData?.connection?.channel;

		if (!effectiveChannel) {
			setContacts([]);
			setHasMoreContacts(false);
			return;
		}

		setContactsPage(1);
		setLoadingContacts(true);
		setContacts([]);

		const tagIds = selectedTagsRef.current
			.map((tag) => (typeof tag === "object" ? tag?.id : tag))
			.filter((id) => id != null);

		const listParams = {
			searchParam: searchParamRef.current,
			tags: tagIds,
			channel: effectiveChannel,
			pageSize: 100,
		};

		const MAX_PAGES = 500;

		try {
			let page = 1;
			let hasMore = true;
			const allFormatted = [];

			while (hasMore && page <= MAX_PAGES) {
				const { data } = await api.get("/contacts", {
					params: {
						...listParams,
						pageNumber: page,
					},
				});
				allFormatted.push(...formatContactsPage(data.contacts, effectiveChannel));
				hasMore = Boolean(data.hasMore);
				page += 1;
			}
			setContacts(allFormatted);
			setToTransferContacts((prev) => {
				if (!prev.length) return prev;
				const formattedByKey = new Map();
				for (const contact of allFormatted) {
					const key = bulkContactKey(contact);
					if (key) formattedByKey.set(key, contact);
				}
				return prev.map((contact) => {
					const key = bulkContactKey(contact);
					return (key && formattedByKey.get(key)) || contact;
				});
			});
			setHasMoreContacts(false);
			setContactsPage(page - 1);
		} catch (error) {
			toastError(error);
			setContacts([]);
			setHasMoreContacts(false);
		} finally {
			setLoadingContacts(false);
		}
	}, [bulkMessage?.bulkMessagingData?.connection?.channel, channel]);

	const getTags = useCallback(async () => {
		try {
			const { data } = await api.get("/tags");
			setTags(data.tags);
		} catch (error) {
			toastError(error);
		}
	}, []);

	const handleMessage = (e) => {
		const { value } = e.target;
		if (value[value.length - 1] === "$") {
			setVariablesModalOpen(true);
			return;
		}

		// const formatedMessage = value.replace(
		//   new RegExp(`(\\${defaultVariables.join("|\\")})`, "g"),
		//   (match) => variables[match]
		// );

		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				message: value,
			},
		});
	};

	const handleAddEmoji = (e) => {
		const emoji = e.native;
		// setInputMessage(prevState => prevState + emoji);
		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				message: bulkMessage.bulkMessagingData.message + emoji,
			},
		});
	};

	useEffect(() => {
		const templateText = bulkMessage.bulkMessagingData.templateText;
		const length = countTemplateParams(templateText);
		setTemplateParamsLength(length);

		const stored = bulkMessage.bulkMessagingData.templateParams;
		if (Array.isArray(stored) && stored.length === length) {
			setTemplateParams(stored);
			return;
		}
		setTemplateParams(Array(length).fill(""));
	}, [
		bulkMessage.bulkMessagingData.templateId,
		bulkMessage.bulkMessagingData.customTemplateId,
		bulkMessage.bulkMessagingData.templateText,
	]);

	useEffect(() => {
		if (!bulkMessageId || user?.companyId == null) return;

		(async () => {
			setLoadingContacts(true);
			try {
				const { data } = await apiV2.get(`/bulk-messaging/${bulkMessageId}`, {
					params: { companyId: user.companyId },
				});

				let parsedTp = [];
				try {
					const raw = data.bulkMessagingData?.templateParams;
					if (Array.isArray(raw)) {
						parsedTp = raw;
					} else if (typeof raw === "string" && raw.trim() && raw !== "null") {
						parsedTp = JSON.parse(raw);
					}
				} catch {
					parsedTp = [];
				}

				setBulkMessage({
					...data,
					contacts: Array.isArray(data?.contacts) ? data.contacts : [],
					bulkMessagingData: {
						...data.bulkMessagingData,
						templateParams: parsedTp,
						tagIds: Array.isArray(data.bulkMessagingData?.tagIds)
							? data.bulkMessagingData.tagIds.map((id) => Number(id)).filter((id) => id > 0)
							: [],
					},
				});
				setChannel(data.bulkMessagingData?.connection?.channel);
				setTriggerType(
					data.bulkMessagingData.scheduled ? "scheduled" : "immediate"
				);
				setTemplateParamsLength(parsedTp?.length || 0);
				setTemplateParams(parsedTp || []);
				setLoadingContacts(false);
			} catch (error) {
				toastError(error);
				setLoadingContacts(false);
			}
		})();
	}, [bulkMessageId, user?.companyId]);

	useEffect(() => {
		getTags();
	}, [getTags]);

	useEffect(() => {
		if (activeStep !== 1) return;

		const effectiveChannel =
			channel || bulkMessage?.bulkMessagingData?.connection?.channel;

		if (effectiveChannel) {
			getContacts();
		} else {
			setContacts([]);
			setHasMoreContacts(false);
		}
	}, [activeStep, getContacts, channel, bulkMessage?.bulkMessagingData?.connection?.channel]);

	// const renderText = (text) => {
	//   const styledText = <></>;

	//   console.log("teste render text", text.split(" "));

	//   text.split(" ").forEach((word) => {
	//     if (word.startsWith("${") && word.endsWith("}")) {
	//       // styledTextArray.push(
	//       //   <Chip label={word} />
	//       // );

	//     } else {
	//       styledTextArray.push(<span>{`${word} `}</span>);
	//     }
	//   });

	//   return styledTextArray;
	// };

	const pageTitle = bulkMessageId
		? i18n.t("bulkMessaging.wizard.pageTitleEdit")
		: i18n.t("bulkMessaging.wizard.pageTitleAdd");

	const campaignContacts = useMemo(() => {
		const list = Array.isArray(bulkMessage?.contacts) ? bulkMessage.contacts : [];
		const effectiveChannel =
			channel || bulkMessage?.bulkMessagingData?.connection?.channel;
		if (effectiveChannel === "whatsapp" || effectiveChannel === "whatsappcloud") {
			return list.filter(isWhatsappBulkContact);
		}
		return list;
	}, [bulkMessage?.contacts, bulkMessage?.bulkMessagingData?.connection?.channel, channel]);

	const displayChannel =
		channel || bulkMessage?.bulkMessagingData?.connection?.channel;

	const isContinueDisabled =
		(["VIDEO", "DOCUMENT", "IMAGE"].includes(
			String(
				bulkMessage?.bulkMessagingData?.template?.components?.find(
					(c) => String(c?.type || "").toUpperCase() === "HEADER"
				)?.format || ""
			).toUpperCase()
		) &&
			!bulkMessage?.bulkMessagingData?.mediaPath) ||
		bulkMessage?.bulkMessagingData?.name === "" ||
		!bulkMessage?.bulkMessagingData?.name ||
		((bulkMessage?.bulkMessagingData?.message === "" ||
			!bulkMessage?.bulkMessagingData?.message) &&
			!bulkMessage?.bulkMessagingData?.templateId &&
			!bulkMessage?.bulkMessagingData?.customTemplateId) ||
		!bulkMessage?.bulkMessagingData?.connectionId ||
		(templateParamsLength > 0 &&
			templateParams.some((param) => !String(param ?? "").trim()));

	const renderWizardFooter = () => {
		if (allStepsCompleted()) {
			return (
				<button type="button" className="btn btn-sub-gray ms-auto" onClick={handleReset}>
					{i18n.t("bulkMessaging.modal.reset")}
				</button>
			);
		}

		return (
			<>
				{activeStep > 0 ? (
					<button type="button" className="btn btn-sub-gray" onClick={handleBack}>
						{i18n.t("bulkMessaging.wizard.previousStep")}
					</button>
				) : (
					<span aria-hidden="true" />
				)}
				{activeStep === steps.length - 1 ? (
					<button type="button" className="btn btn-primary" onClick={handleFinish}>
						{i18n.t("bulkMessaging.modal.finish")}
					</button>
				) : (
					<button
						type="button"
						className="btn btn-primary"
						onClick={handleComplete}
						disabled={isContinueDisabled}
					>
						{i18n.t("bulkMessaging.modal.continue")}
					</button>
				)}
			</>
		);
	};

	return (
		<div className="app-admin-page">
			<VariablesModal
				open={variablesModalOpen}
				handleClose={() => setVariablesModalOpen(false)}
				variables={defaultVariables}
				bulkMessage={bulkMessage}
				setBulkMessage={setBulkMessage}
			/>

			<div className="app-admin-page__scroll">
				<AppPageTitle
					title={pageTitle}
					subtitle={i18n.t("bulkMessaging.subtitle")}
				/>

				<div className="card bulk-messaging-wizard-card">
					<div className="card-header flex-wrap items-center gap-3 px-5 pt-5 pb-4">
						<button
							type="button"
							className="inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-sm font-medium text-primary hover:underline"
							onClick={handleCancel}
						>
							<span aria-hidden>←</span>
							{i18n.t("bulkMessaging.wizard.backToList")}
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col">
						<div className="card-body px-5 pb-5 pt-0">
							<nav className="app-import-stepper shrink-0" aria-label={pageTitle}>
						{steps.map((label, index) => (
							<div
								key={label}
								className={[
									"app-import-step",
									index === activeStep && "is-active",
									index < activeStep && "is-done",
									completed[index] && index !== activeStep && "is-done",
								]
									.filter(Boolean)
									.join(" ")}
							>
								<span className="app-import-step__index">{index + 1}</span>
								<span className="app-import-step__label">{label}</span>
							</div>
						))}
							</nav>

							{allStepsCompleted() ? (
								<div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
									<p className="m-0 text-sm text-default-600 dark:text-default-400">
										{i18n.t("bulkMessaging.modal.allStepsDone")}
									</p>
								</div>
							) : (
								<div
									className="bulk-messaging-wizard bulk-messaging-wizard__panel w-full"
								>
														{activeStep === 0 && (
								<BulkMessagingCampaignStep
									bulkMessage={bulkMessage}
									setBulkMessage={setBulkMessage}
									channel={channel}
									setChannel={setChannel}
									whatsApps={whatsApps}
									templateParams={templateParams}
									setTemplateParams={setTemplateParams}
									templateParamsLength={templateParamsLength}
									handleMessage={handleMessage}
									handleAddEmoji={handleAddEmoji}
									showEmoji={showEmoji}
									setShowEmoji={setShowEmoji}
									darkMode={darkMode}
									handleUpdateMedia={handleUpdateMedia}
									setContacts={setContacts}
									setSelectedContacts={setSelectedContacts}
									setHasMoreContacts={setHasMoreContacts}
									setContactsPage={setContactsPage}
									setToTransferContacts={setToTransferContacts}
									loadingContacts={loadingContacts}
									tags={tags}
								/>
							)}

							{activeStep === 1 && (
								<BulkMessagingContactsStep
									addContactsType={addContactsType}
									setAddContactsType={setAddContactsType}
									searchParam={searchParam}
									setSearchParam={setSearchParam}
									setContactsPage={setContactsPage}
									selectedTags={selectedTags}
									setSelectedTags={setSelectedTags}
									getContacts={getContacts}
									contacts={contacts}
									setContacts={setContacts}
									loadingContacts={loadingContacts}
									toTransferContacts={toTransferContacts}
									setToTransferContacts={setToTransferContacts}
									bulkMessage={bulkMessage}
									setBulkMessage={setBulkMessage}
									campaignContacts={campaignContacts}
									displayChannel={displayChannel}
									handleCreateContact={handleCreateContact}
									newUser={newUser}
									setNewUser={setNewUser}
									csvName={csvName}
									setContactsByCSV={setContactsByCSV}
									lastCsvImportCount={lastCsvImportCount}
									csvImportRejected={csvImportRejected}
									includedPanelRef={includedPanelRef}
									selectedContacts={selectedContacts}
									setSelectedContacts={setSelectedContacts}
								/>
							)}

							{activeStep === 2 && (
								<BulkMessagingReviewStep
									bulkMessage={bulkMessage}
									setBulkMessage={setBulkMessage}
									bulkMessageId={bulkMessageId}
									triggerType={triggerType}
									setTriggerType={setTriggerType}
									getDateFormat={getDateFormat}
									message={bulkMessage?.bulkMessagingData?.message}
									darkMode={darkMode}
									tags={tags}
								/>
							)}

								</div>
							)}

						</div>
					</div>

					<div className="card-footer flex flex-wrap items-center justify-between gap-3 px-5 py-4">
						{renderWizardFooter()}
					</div>
				</div>
			</div>
		</div>
	);
}
